import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import { getResourcesUrl } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';
import SubPanelButton from '@cartwhl/ui/molecules/SubPanelButton';

import { characterPickerStore, sceneSettingsStore, uiStore } from 'stores';

import { FACE_PICKER_SUBPANEL_KEY } from '../constants';
import { getFaceDisplayName } from '../utils';

export default function FacePicker() {
  const ref = useRef<HTMLButtonElement | null>(null);
  const { subPanelKey } = useSnapshot(uiStore.state);
  const isOpen = subPanelKey === FACE_PICKER_SUBPANEL_KEY;

  const { faceExpressions } = useSnapshot(characterPickerStore.state);

  const {
    characterSettings: { faceExpression },
  } = useSnapshot(sceneSettingsStore.state);

  // Map old face expressions to new ones when needed
  const currentFace = sceneSettingsStore.mapOldFaceToNew(faceExpression);
  const displayName = getFaceDisplayName(currentFace);
  const baseURL = getResourcesUrl(false);

  // if the faceExpression is an old face, update the scene settings to the new face
  if (currentFace !== faceExpression) {
    sceneSettingsStore.set('characterSettings', 'faceExpression', currentFace);
  }

  return (
    <>
      <SubPanelButton
        isOpen={isOpen}
        onClick={() => {
          uiStore.setSubPanelKey(isOpen ? '' : FACE_PICKER_SUBPANEL_KEY);
        }}
        ref={ref}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <div className="-my-2 -ml-2">
              <img
                className="aspect-square w-[60px] rounded-lg object-cover"
                src={`${baseURL}/images/static_faces_icons/${currentFace}.webp`}
                alt={displayName}
              />
            </div>
            <div className="text-left">
              <Text variant="studio/body/medium/sm" as="h2">
                Face
              </Text>
              <Text
                variant="studio/body/medium/sm"
                as="div"
                className="text-gray-45"
              >
                {displayName}
              </Text>
            </div>
          </div>
          <ChevronRightIcon className="text-gray-45" />
        </div>
      </SubPanelButton>
      {isOpen && (
        <SubPanel
          id={FACE_PICKER_SUBPANEL_KEY}
          elementToTrack={ref.current}
          closePanel={() => uiStore.setSubPanelKey('')}
          subPanelKey={subPanelKey}
        >
          <div className="grid grid-cols-4 gap-2">
            {faceExpressions.map((face) => {
              // Map any legacy faces to new format
              const newFace = sceneSettingsStore.mapOldFaceToNew(face);

              return (
                <AnimationPreview
                  key={newFace}
                  active={currentFace === newFace}
                  onClick={() => {
                    sceneSettingsStore.set(
                      'characterSettings',
                      'faceExpression',
                      newFace,
                    );
                  }}
                  mediaPath={`${baseURL}/images/static_faces_icons/${newFace}.webp`}
                  mediaType="image"
                />
              );
            })}
          </div>
        </SubPanel>
      )}
    </>
  );
}
